<template>
  <EdroneSlider
    :open="isOpen"
    :show-close-icon="false"
    :width="460"
    :max-height="600"
    unique-id="tour-slider"
    class="tw:!pt-0 tw:overflow-hidden"
  >
    <TourHero :steps="TOUR_STEPS" :current-step-index="currentStepIndex" @step-click="goToStep" />

    <div class="tw:flex tw:flex-col tw:gap-5 tw:mt-32">
      <TourStepContent
        :title="getStepTitle(currentStep)"
        :description="getStepDescription(currentStep)"
        :step-index="currentStepIndex"
        :transition-name="transitionName"
      />

      <TourActions
        :is-first-step="isFirstStep"
        :is-last-step="isLastStep"
        @prev="handlePrev"
        @next="handleNext"
        @finish="handleFinish"
      />
    </div>
  </EdroneSlider>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import EdroneSlider from "edrone-components/EdroneSlider.vue";
import { useTourSlider } from "./useTourSlider";
import { TOUR_STEPS, TourStep } from "./TourSlider.constants";
import { useRouter } from "vue-router";
import TourHero from "./TourHero.vue";
import TourStepContent from "./TourStepContent.vue";
import TourActions from "./TourActions.vue";

const trans = window._trans;
const router = useRouter();
const transitionName = ref("slide-up");

function getStepTitle(step: TourStep): string {
  return trans._onboarding.tour.steps[step.id as keyof typeof trans._onboarding.tour.steps]?.title ?? step.id;
}

function getStepDescription(step: TourStep): string {
  return trans._onboarding.tour.steps[step.id as keyof typeof trans._onboarding.tour.steps]?.description ?? "";
}

const {
  isOpen,
  currentStep,
  currentStepIndex,
  isFirstStep,
  isLastStep,
  nextStep,
  prevStep,
  completeTour,
  goToStep,
} = useTourSlider();

function handleNext(): void {
  nextStep();
}

function handlePrev(): void {
  prevStep();
}

function handleFinish(): void {
  shotConfetti();
  completeTour();
}

function shotConfetti(): void {
  if (!window.confetti) return;

  const colors = ["#006CFA", "#542CFA", "#A700FA", "#FFBA08", "#00CC52"];

  window.confetti({
    particleCount: 80,
    spread: 70,
    origin: { x: 0.1, y: 0.6 },
    colors,
    angle: 60,
    startVelocity: 45,
    gravity: 0.8,
    ticks: 300,
  });

  window.confetti({
    particleCount: 80,
    spread: 70,
    origin: { x: 0.9, y: 0.6 },
    colors,
    angle: 120,
    startVelocity: 45,
    gravity: 0.8,
    ticks: 300,
  });

  setTimeout(() => {
    window.confetti?.({
      particleCount: 50,
      spread: 100,
      origin: { x: 0.5, y: 0.5 },
      colors,
      startVelocity: 35,
      gravity: 0.6,
      ticks: 250,
    });
  }, 150);
}

function navigateToCurrentStep(): void {
  router.push(currentStep.value.path);
}

watch(currentStepIndex, (newIndex, oldIndex) => {
  transitionName.value = newIndex > oldIndex ? "slide-up" : "slide-down";

  if (isOpen.value) {
    setTimeout(() => {
      navigateToCurrentStep();
    }, 500);
  }
});

watch(isOpen, (newValue) => {
  if (newValue) {
    navigateToCurrentStep();
  }
});
</script>
